// Mobile version of How It Works section with campus carousel
import React, { useState, useEffect } from 'react';
import SystemMap from '../../../../components/landing/SystemMap';
import './HowItWorksCampusSection.css';

/** Image assets */
const IMG_BASE = '/landing_page_hero/how_it_works_section';

export default function HowItWorksCampusSection() {
  const [activeStep, setActiveStep] = useState(1);
  const [showScoutInMagic, setShowScoutInMagic] = useState(true);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [showPdfInMagic, setShowPdfInMagic] = useState(false);
  const railRef = React.useRef(null);

  // Handle scroll-based step detection
  const handleRailScroll = () => {
    if (!railRef.current) return;
    const rail = railRef.current;
    const slideWidth = rail.offsetWidth; // Full width per slide
    const scrollPos = rail.scrollLeft;
    const index = Math.round(scrollPos / slideWidth);
    if (index + 1 !== activeStep) {
      setActiveStep(index + 1);
    }
  };

  // Handle Scout appearance in Step 2
  useEffect(() => {
    if (activeStep === 2) {
      setShowScoutInMagic(true);
      setIsFadingOut(false);
      setShowPdfInMagic(false);

      const fadeTimer = setTimeout(() => {
        setIsFadingOut(true);
      }, 2600);

      const hideTimer = setTimeout(() => {
        setShowScoutInMagic(false);
        setShowPdfInMagic(true);
      }, 3000);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(hideTimer);
      };
    } else {
      setShowScoutInMagic(true);
      setIsFadingOut(false);
      setShowPdfInMagic(false);
    }
  }, [activeStep]);

  const steps = [
    {
      id: 1,
      shortLabel: 'Upload',
      caption: 'Drop your syllabus',
      title: '1. Upload your syllabus',
      bullets: [
        'Supports PDFs and most text-based formats',
        'Handles multiple classes in one go',
        'No manual data entry or spreadsheet work',
      ],
      image: `${IMG_BASE}/scout_holding_document.png`,
      visualType: 'scout',
    },
    {
      id: 2,
      shortLabel: 'Magic Builds',
      caption: 'Scout creates your campus',
      title: '2. DocuHounds builds your semester',
      bullets: [
        'Auto-generated calendar with synced due dates',
        'Timeline view for exams, projects, and readings',
        "Clickable cards that jump into Scout's AI tools",
      ],
      scout: `${IMG_BASE}/scout_tossing_document.png`,
      doc: `${IMG_BASE}/pdf_icon.png`,
      rings: `${IMG_BASE}/magic_ripple.png`,
      screenshot: '/landing_page_hero/screenshots/Timeline__Screenshot_Productivity_Tool.png',
      visualType: 'screenshot',
    },
    {
      id: 3,
      shortLabel: 'Explore Campus',
      caption: 'Interact with everything',
      title: '3. Explore your workspace',
      bullets: [
        'Everything your syllabus creates lives in one connected workspace',
        'Jump from course → timeline → tools without hunting',
        'Your progress stays organized across every class',
      ],
      visualType: 'system',
    },
  ];

  // Scroll to step when pill is clicked
  const scrollToStep = (stepId) => {
    setActiveStep(stepId);
    if (!railRef.current) return;
    const rail = railRef.current;
    const slideWidth = rail.offsetWidth;
    rail.scrollTo({
      left: (stepId - 1) * slideWidth,
      behavior: 'smooth',
    });
  };

  return (
    <section id="m-how-it-works-campus" className="m-how-campus">
      {/* Header */}
      <header className="m-how-campus__header">
        <h2>How it works</h2>
        <p className="m-how-campus__subhead">
          One syllabus upload turns into a living campus: dashboard, timeline,
          and AI study tools you can actually use.
        </p>
      </header>

      {/* Step Pills - Sticky */}
      <div className="m-how-campus__pills">
        {steps.map((step, index) => (
          <button
            key={step.id}
            className={`m-how-campus__pill ${
              activeStep === step.id ? 'm-how-campus__pill--active' : ''
            }`}
            onClick={() => scrollToStep(step.id)}
          >
            <span className="m-how-campus__pill-number">{index + 1}</span>
            <span className="m-how-campus__pill-label">{step.shortLabel}</span>
          </button>
        ))}
      </div>

      {/* Swipeable Rail */}
      <div 
        className="m-how-campus__rail" 
        ref={railRef}
        onScroll={handleRailScroll}
      >
        {/* Step 1: Upload */}
        <div className="m-how-campus__slide">
          <div className="m-how-campus__card">
            <div className="m-how-campus__content">
              <h3 className="m-how-campus__title">{steps[0].title}</h3>
            </div>
            <div className="m-how-campus__visual">
              <div className="m-how-campus__upload-scene">
                <div className="m-how-campus__sparkles" aria-hidden="true" />
                <img
                  src={steps[0].image}
                  alt="Scout holding a syllabus"
                  className="m-how-campus__scout-upload"
                />
              </div>
            </div>
            <div className="m-how-campus__content">
              <ul className="m-how-campus__bullets">
                {steps[0].bullets.map((bullet, idx) => (
                  <li key={idx}>{bullet}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Step 2: Magic Builds */}
        <div className="m-how-campus__slide">
          <div className="m-how-campus__card">
            <div className="m-how-campus__content">
              <h3 className="m-how-campus__title">{steps[1].title}</h3>
            </div>
            <div className="m-how-campus__visual">
              <div className="m-how-campus__magic-scene">
                <div className="m-how-campus__orbit-glow" aria-hidden="true" />
                <img
                  src={steps[1].rings}
                  alt=""
                  className="m-how-campus__magic-ripple"
                  aria-hidden="true"
                />
                <img
                  src={steps[1].doc}
                  alt="Syllabus being processed"
                  className={`m-how-campus__magic-doc ${
                    showPdfInMagic ? 'm-how-campus__magic-doc--visible' : ''
                  }`}
                />
                {showScoutInMagic && (
                  <img
                    src={steps[1].scout}
                    alt="Scout tossing syllabus"
                    className={`m-how-campus__scout-magic ${
                      isFadingOut ? 'm-how-campus__scout-magic--fading' : ''
                    }`}
                  />
                )}
              </div>
            </div>
            <div className="m-how-campus__content">
              <ul className="m-how-campus__bullets">
                {steps[1].bullets.map((bullet, idx) => (
                  <li key={idx}>{bullet}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Step 3: Explore Workspace */}
        <div className="m-how-campus__slide">
          <div className="m-how-campus__card">
            <div className="m-how-campus__content">
              <h3 className="m-how-campus__title">{steps[2].title}</h3>
            </div>
            
            {/* System Map Visual */}
            <div className="m-how-campus__visual m-how-campus__visual--system">
              <SystemMap />
            </div>
            
            {/* Bullets below map */}
            <div className="m-how-campus__content">
              <ul className="m-how-campus__bullets">
                {steps[2].bullets.map((bullet, idx) => (
                  <li key={idx}>{bullet}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Dots */}
      <div className="m-how-campus__dots" style={{ display: 'none' }}>
        {steps.map((step) => (
          <button
            key={step.id}
            className={`m-how-campus__dot ${
              activeStep === step.id ? 'm-how-campus__dot--active' : ''
            }`}
            onClick={() => scrollToStep(step.id)}
            aria-label={`Go to ${step.shortLabel}`}
          />
        ))}
      </div>
    </section>
  );
}
